import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../components/cart/cart-context';
import Price from '../components/price';
import Image from '../components/Image';
import LoadingDots from '../components/loading-dots';
import LogoSquare from '../components/logo-square';
import { DEFAULT_OPTION } from 'lib/constants';
import { buildPayLinkCheckoutUrl } from 'lib/paylink';

const paymentMethods = [
  {
    id: 'kalpe-burok',
    name: 'Burok Pay',
    description: 'Payer avec Burok Mobile Money',
    logo: '/assets/images/BUROKLOGOTWITTER.png',
    colors: ['#007AFF', '#00C6FF']
  },
  {
    id: 'carte-bancaire',
    name: 'Carte bancaire',
    description: 'Payer par carte bancaire',
    logos: ['/assets/images/Mastercard-Logo.png', '/assets/images/Visa-Logo.png'],
    colors: ['#EB001B', '#F79E1B']
  },
  {
    id: 'link-crypto',
    name: 'Link Crypto',
    description: 'Connecter votre portefeuille crypto',
    logo: `${import.meta.env.VITE_SUPABASE_URL || ''}/storage/v1/object/public/Files/Logos/LinkBlackLogo.png`,
    colors: ['#5856D6', '#7875FF']
  },
  {
    id: 'thirdweb',
    name: 'Thirdweb',
    description: 'Payer via Thirdweb',
    logo: '/assets/images/Thirdweb.png',
    colors: ['#9945FF', '#14F195']
  },
  {
    id: 'base-pay',
    name: 'Base Pay',
    description: 'Payer via Coinbase Wallet',
    logo: `${import.meta.env.VITE_SUPABASE_URL || ''}/storage/v1/object/public/Files/Logos/coinbase.webp`,
    colors: ['#0052FF', '#6C8EFF']
  }
];

export default function PaymentPage() {
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');

  // Récupérer les données depuis l'état ou localStorage
  const formData = location.state || JSON.parse(localStorage.getItem('checkoutFormData') || '{}');
  const shippingData = location.state?.shippingData || JSON.parse(localStorage.getItem('shippingData') || '{}');

  if (!cart || cart.lines.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-neutral-900 dark:text-white">
            Votre panier est vide
          </h1>
          <Link
            to="/"
            className="inline-block rounded-md bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
          >
            Continuer les achats
          </Link>
        </div>
      </div>
    );
  }

  const shippingPriceNum = parseFloat(shippingData.shippingPrice || '0');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPaymentMethod) {
      alert('Veuillez sélectionner une méthode de paiement');
      return;
    }

    setLoading(true);

    const payLinkUrl = buildPayLinkCheckoutUrl(cart, shippingPriceNum);
    if (payLinkUrl) {
      window.location.href = payLinkUrl;
      return;
    }

    alert(
      'Impossible de générer le lien de paiement. Vérifiez que votre panier provient bien d’un kiosk et que le propriétaire a un username configuré.'
    );
    setLoading(false);
  };

  const handleBackToShipping = () => {
    navigate('/shipping', { state: { ...formData, shippingData } });
  };

  // Construire l'adresse complète
  const fullAddress = [
    formData.address,
    formData.apartment && `Appartement ${formData.apartment}`,
    formData.city,
    formData.state,
    formData.postalCode,
    formData.country
  ].filter(Boolean).join(', ');

  const shippingPrice = shippingData.shippingPrice || '0';
  const totalAmount = (
    parseFloat(cart.cost.totalAmount.amount) +
    parseFloat(shippingPrice)
  ).toString();

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="mx-auto flex max-w-7xl">
        {/* Formulaire de paiement - Colonne gauche */}
        <div className="flex-1 border-r border-neutral-200 dark:border-neutral-800">
          <div className="mx-auto max-w-2xl px-6 py-12">
            {/* Logo ShopLink */}
            <div className="mb-4 flex justify-start">
              <Link to="/">
                <LogoSquare />
              </Link>
            </div>
            
            {/* Breadcrumb */}
            <div className="mb-8 flex items-center justify-start space-x-2 text-sm text-neutral-500 dark:text-neutral-400">
              <Link
                to="/checkout"
                className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                Information
              </Link>
              <span>/</span>
              <Link
                to="/shipping"
                className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                onClick={handleBackToShipping}
              >
                Livraison
              </Link>
              <span>/</span>
              <span className="font-medium text-neutral-900 dark:text-white">Paiement</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Résumé Contact, Ship to et Shipping */}
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Contact</p>
                    <p className="mt-1 text-sm font-medium text-neutral-900 dark:text-white">
                      {formData.email || formData.phoneNumber ? (
                        formData.email || `${formData.phoneCode} ${formData.phoneNumber}`
                      ) : (
                        'Non renseigné'
                      )}
                    </p>
                  </div>
                  <Link
                    to="/checkout"
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    Modifier
                  </Link>
                </div>

                <div className="flex items-start justify-between rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                  <div className="flex-1">
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Livrer à</p>
                    <p className="mt-1 text-sm font-medium text-neutral-900 dark:text-white">
                      {fullAddress || 'Non renseigné'}
                    </p>
                  </div>
                  <Link
                    to="/checkout"
                    className="ml-4 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    Modifier
                  </Link>
                </div>

                <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Méthode</p>
                    <p className="mt-1 text-sm font-medium text-neutral-900 dark:text-white">
                      {shippingData.shippingMethod || 'Non renseigné'}
                    </p>
                  </div>
                  <Link
                    to="/shipping"
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    onClick={handleBackToShipping}
                  >
                    Modifier
                  </Link>
                </div>
              </div>

              {/* Section Payment Method */}
              <div>
                <h2 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-white">
                  Paiement
                </h2>
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <label
                      key={method.id}
                      className={`flex cursor-pointer items-center space-x-4 rounded-md border-2 p-4 transition-all ${
                        selectedPaymentMethod === method.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-neutral-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedPaymentMethod === method.id}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="h-4 w-4 border-neutral-300 text-blue-600 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-700"
                      />
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-white p-2 dark:bg-neutral-700">
                        {method.id === 'carte-bancaire' && method.logos ? (
                          <div className="flex h-full w-full items-center justify-center gap-1">
                            <img
                              src={method.logos[0]}
                              alt="Mastercard"
                              className="h-4 w-auto object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <img
                              src={method.logos[1]}
                              alt="Visa"
                              className="h-4 w-auto object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        ) : (
                          <img
                            src={method.logo}
                            alt={method.name}
                            className="h-full w-full object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`text-base font-medium text-neutral-900 dark:text-white ${method.id === 'kalpe-burok' ? 'font-bold' : ''}`}>
                          {method.name}
                        </p>
                        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                          {method.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center justify-between pt-4">
                <Link
                  to="/shipping"
                  onClick={handleBackToShipping}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  ← Retour à la livraison
                </Link>
                <button
                  type="submit"
                  disabled={loading || !selectedPaymentMethod}
                  className="rounded-md bg-blue-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <LoadingDots className="bg-white" /> : 'Finaliser la commande'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Order Summary - Colonne droite */}
        <div className="w-96 border-l border-neutral-200 dark:border-neutral-800">
          <div className="sticky top-0 h-screen overflow-y-auto px-6 py-12">
            <h2 className="mb-6 text-lg font-semibold text-neutral-900 dark:text-white">
              Résumé de commande
            </h2>

            <div className="space-y-4">
              {cart.lines.map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="relative h-16 w-16 shrink-0">
                    <div className="h-full w-full overflow-hidden rounded-md border-2 border-blue-500 bg-neutral-100 dark:bg-neutral-800">
                      <Image
                        src={item.merchandise.product.featuredImage?.url}
                        alt={item.merchandise.product.title}
                        width={64}
                        height={64}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    {item.quantity > 1 && (
                      <div className="absolute -right-1 -top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white shadow-sm">
                        {item.quantity}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {item.merchandise.product.title}
                    </p>
                    {item.merchandise.title !== DEFAULT_OPTION && (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                        {item.merchandise.title}
                      </p>
                    )}
                    <Price
                      amount={item.cost.totalAmount.amount}
                      currencyCode={item.cost.totalAmount.currencyCode}
                      className="mt-1 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3 border-t border-neutral-200 pt-6 dark:border-neutral-700">
              <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                <span>Sous-total</span>
                <Price
                  amount={cart.cost.subtotalAmount.amount}
                  currencyCode={cart.cost.subtotalAmount.currencyCode}
                />
              </div>
              <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                <span>Livraison</span>
                <Price
                  amount={shippingPrice}
                  currencyCode={cart.cost.totalAmount.currencyCode}
                />
              </div>
              <div className="flex justify-between border-t border-neutral-200 pt-3 text-xl font-semibold text-neutral-900 dark:border-neutral-700 dark:text-white">
                <span>Total</span>
                <Price
                  amount={totalAmount}
                  currencyCode={cart.cost.totalAmount.currencyCode}
                  className="text-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

